import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface GlobalStats {
  usersCount: number;
  brandsCount: number;
  institutionsCount: number;
  totalDonated: number;
}

export interface HomeMetrics {
  gmvConfirmedBrl: number | null;
  approvedCommissionBrl: number | null;
  feedProductsTotal: number | null;
  comparableProducts: number | null;
  products50Plus: number | null;
  worthwhileProducts: number | null;
  activeCouponCodes: number | null;
  usersCount: number | null;
  brandsCount: number | null;
  institutionsCount: number | null;
  refreshedAt: string | null;
}

export const useHomeMetrics = () => {
  return useQuery({
    queryKey: ["home-metrics"],
    queryFn: async (): Promise<HomeMetrics> => {
      const { data, error } = await supabase.rpc("get_home_metrics_v2");
      if (error) throw error;
      const row = data?.[0];
      const finiteNumber = (value: unknown): number | null => {
        if (value === null || value === undefined || value === "") return null;
        const parsed = typeof value === "number" ? value : Number(String(value).trim());
        return Number.isFinite(parsed) ? parsed : null;
      };
      return {
        gmvConfirmedBrl: finiteNumber(row?.gmv_confirmed_brl),
        approvedCommissionBrl: finiteNumber(row?.approved_commission_brl),
        feedProductsTotal: finiteNumber(row?.feed_products_total),
        comparableProducts: finiteNumber(row?.comparable_products),
        products50Plus: finiteNumber(row?.products_50_plus),
        worthwhileProducts: finiteNumber(row?.worthwhile_products),
        activeCouponCodes: finiteNumber(row?.active_coupon_codes),
        usersCount: finiteNumber(row?.users_count),
        brandsCount: finiteNumber(row?.brands_count),
        institutionsCount: finiteNumber(row?.institutions_count),
        refreshedAt: row?.refreshed_at ?? null,
      };
    },
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: 3,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
};

export const useGlobalStats = (options?: { enabled?: boolean }) => {
  return useQuery({
    enabled: options?.enabled ?? true,

    queryKey: ["global-stats"],
    queryFn: async (): Promise<GlobalStats> => {
      console.log("[useGlobalStats] Fetching global statistics via RPC...");
      
      const { data, error } = await supabase.rpc("get_global_stats");

      if (error) {
        console.error("[useGlobalStats] RPC error:", error);
        throw error;
      }

      const row = data?.[0];
      const stats: GlobalStats = {
        usersCount: Number(row?.users_count ?? 0),
        brandsCount: Number(row?.brands_count ?? 0),
        institutionsCount: Number(row?.institutions_count ?? 0),
        totalDonated: Number(row?.total_donated ?? 0),
      };

      console.log("[useGlobalStats] Stats:", stats);
      return stats;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

// Helper to format currency
export const formatCurrency = (value: number): string => {
  if (value >= 1000000) {
    return `R$ ${(value / 1000000).toFixed(1).replace('.', ',')}M`;
  }
  if (value >= 1000) {
    return `R$ ${(value / 1000).toFixed(1).replace('.', ',')}K`;
  }
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

// Helper to format large numbers
export const formatNumber = (value: number): string => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1).replace('.', ',')}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1).replace('.', ',')}K`;
  }
  return value.toLocaleString('pt-BR');
};
